import { createContext, type ReactNode, useContext, useMemo } from 'react'

/**
 * Default implementation of `window.location.assign`.
 *
 * In test environments, this is replaced with a no-op to prevent unwanted navigation.
 */
const defaultAssign: typeof window.location.assign =
  import.meta.env.MODE === 'test'
    ? (_url) => {}
    : (url) => { window.location.assign(url) }

/**
 * Default implementation of `window.location.reload`.
 *
 * In test environments, this is replaced with a no-op to prevent page reloads.
 */
const defaultReload: typeof window.location.reload =
  import.meta.env.MODE === 'test'
    ? () => {}
    : () => { window.location.reload() }

/**
 * Default implementation of `window.location.replace`.
 *
 * In test environments, this is replaced with a no-op to prevent unwanted navigation.
 */
const defaultReplace: typeof window.location.replace =
  import.meta.env.MODE === 'test'
    ? (_url) => {}
    : (url) => { window.location.replace(url) }

/**
 * Shape of the WindowLocation context.
 *
 * Provides functions for navigation and reloading, abstracted
 * so they can be mocked or replaced in tests.
 */
type WindowLocationContextType = {
  /** Navigate to a new URL (adds to history). Equivalent to `window.location.assign`. */
  assign: typeof window.location.assign
  /** Reload the current page. Equivalent to `window.location.reload`. */
  reload: typeof window.location.reload
  /** Navigate to a new URL (replaces history). Equivalent to `window.location.replace`. */
  replace: typeof window.location.replace
}

// React context
const WindowLocationContext = createContext<WindowLocationContextType>({
  assign: defaultAssign,
  reload: defaultReload,
  replace: defaultReplace,
})

/**
 * React provider for `WindowLocationContext`.
 *
 * Wrap your component tree with this provider if you want to override or mock
 * `window.location` methods (for example, in tests).
 *
 * @example
 * ```tsx
 * import { WindowLocationProvider } from "utils/windowLocation"
 *
 * function App() {
 *   return (
 *     <WindowLocationProvider>
 *       <MyComponent />
 *     </WindowLocationProvider>
 *   )
 * }
 * ```
 *
 * @example Override behavior in tests:
 * ```tsx
 * const mockAssign = vi.fn<typeof window.location.assign>()
 * render(
 *   <WindowLocationProvider assign={mockAssign}>
 *     <MyComponent />
 *   </WindowLocationProvider>
 * )
 *
 * expect(mockAssign).toHaveBeenCalledWith("/login")
 * ```
 */
export const WindowLocationProvider = ({
  children,
  assign,
  reload,
  replace,
}: {
  children: ReactNode
  assign?: typeof window.location.assign
  reload?: typeof window.location.reload
  replace?: typeof window.location.replace
}) => {
  const value = useMemo(() => ({
    assign: assign ?? defaultAssign,
    reload: reload ?? defaultReload,
    replace: replace ?? defaultReplace,
  }), [assign, reload, replace])

  return (
    <WindowLocationContext.Provider value={value}>
      {children}
    </WindowLocationContext.Provider>
  )
}

/**
 * Hook to access `window.location` methods (`assign`, `reload`, `replace`)
 * through context.
 *
 * By using this hook instead of calling `window.location` directly,
 * you make your components easier to test, since you can override
 * the behavior via `WindowLocationProvider`.
 *
 * @example
 * ```tsx
 * import { useWindowLocation } from "utils/windowLocation"
 *
 * function LoginButton() {
 *   const { assign } = useWindowLocation()
 *
 *   return (
 *     <button onClick={() => assign("/login")}>
 *       Go to Login
 *     </button>
 *   )
 * }
 * ```
 */
export const useWindowLocation = () => useContext(WindowLocationContext)
